import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button, Input, Label, Spinner } from "@/components/ui";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Mail,
  ArrowRight,
  Shield,
  FileText,
  Bell,
  Layers,
  PenLine,
  Search,
  type LucideIcon,
} from "lucide-react";
import { AdvisorLogoIcon } from "@/components/icons/AdvisorLogo";

const AGENTS: {
  icon: LucideIcon;
  nameKey: string;
  descKey: string;
  color: string;
  glow: string;
}[] = [
  {
    icon: FileText,
    nameKey: "login.agents.reporter",
    descKey: "login.agents.reporterDesc",
    color: "text-blue-400",
    glow: "shadow-blue-400/60",
  },
  {
    icon: Bell,
    nameKey: "login.agents.monitor",
    descKey: "login.agents.monitorDesc",
    color: "text-amber-400",
    glow: "shadow-amber-400/60",
  },
  {
    icon: Layers,
    nameKey: "login.agents.builder",
    descKey: "login.agents.builderDesc",
    color: "text-purple-400",
    glow: "shadow-purple-400/60",
  },
  {
    icon: PenLine,
    nameKey: "login.agents.creative",
    descKey: "login.agents.creativeDesc",
    color: "text-teal-400",
    glow: "shadow-teal-400/60",
  },
  {
    icon: Search,
    nameKey: "login.agents.researcher",
    descKey: "login.agents.researcherDesc",
    color: "text-green-400",
    glow: "shadow-green-400/60",
  },
];

const ORBIT_DURATION = 10; // seconds for full orbit
const STEP_DURATION = ORBIT_DURATION / AGENTS.length; // time per agent

function AgentLoopVisualization() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const idx = Math.floor((elapsed % ORBIT_DURATION) / STEP_DURATION) % AGENTS.length;
      setActiveIndex(idx);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative flex items-center justify-center w-full h-full">
      <div className="relative w-80 h-80 lg:w-[420px] lg:h-[420px]">
        {/* Outer ring */}
        <div className="absolute inset-0 rounded-full border border-white/[0.08]" />
        {/* Animated dashed ring */}
        <div className="absolute inset-3 rounded-full border border-dashed border-white/[0.06] animate-[spin_30s_linear_infinite]" />
        {/* Inner ring */}
        <div className="absolute inset-10 rounded-full border border-white/[0.04]" />

        {/* Orbiting dot - synced with agent highlight */}
        <div
          className="absolute inset-0"
          style={{
            animation: `spin ${ORBIT_DURATION}s linear infinite`,
          }}
        >
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-teal-400 shadow-[0_0_12px_4px_rgba(96,165,250,0.5)]" />
        </div>

        {/* Center content */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-blue-500/20 to-teal-500/20 border border-white/10 flex items-center justify-center backdrop-blur-sm">
              <div className="w-6 h-6 rounded-full bg-gradient-to-r from-blue-400 to-teal-400 animate-pulse" />
            </div>
            <p className="text-xs text-white/40 font-medium tracking-widest uppercase">
              {t("login.agentLoop")}
            </p>
          </div>
        </div>

        {/* Agent nodes positioned around the circle */}
        {AGENTS.map((agent, i) => {
          const angle = (i * 72 - 90) * (Math.PI / 180);
          const radius = 50;
          const x = 50 + radius * Math.cos(angle);
          const y = 50 + radius * Math.sin(angle);
          const isActive = activeIndex === i;
          const Icon = agent.icon;

          return (
            <div
              key={agent.nameKey}
              className="absolute z-10"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: "translate(-50%, -50%)",
              }}
            >
              <div className="flex flex-col items-center">
                <div
                  className={`
                    w-11 h-11 rounded-xl flex items-center justify-center backdrop-blur-sm
                    transition-all duration-500 ease-out mb-2.5
                    ${
                      isActive
                        ? `bg-white/[0.15] border-2 border-white/30 scale-125 ${agent.glow} shadow-lg`
                        : "bg-white/10 border border-white/15"
                    }
                  `}
                >
                  <Icon
                    className={`w-5 h-5 transition-all duration-500 ${
                      isActive ? agent.color : "text-white/70"
                    }`}
                  />
                </div>
                <p
                  className={`text-xs font-semibold transition-all duration-500 ${
                    isActive ? "text-white" : "text-white/70"
                  }`}
                >
                  {t(agent.nameKey)}
                </p>
                <p
                  className={`text-[10px] transition-all duration-500 max-w-[100px] text-center ${
                    isActive ? "text-white/50" : "text-white/35"
                  }`}
                >
                  {t(agent.descKey)}
                </p>
              </div>
            </div>
          );
        })}

        {/* Gradient arc SVG */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          fill="none"
        >
          <circle
            cx="50"
            cy="50"
            r="50"
            stroke="url(#loopGradient)"
            strokeWidth="0.3"
            strokeDasharray="3 6"
            className="animate-[spin_20s_linear_infinite_reverse]"
            style={{ transformOrigin: "center" }}
          />
          <defs>
            <linearGradient
              id="loopGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="rgba(96,165,250,0.4)" />
              <stop offset="50%" stopColor="rgba(45,212,191,0.4)" />
              <stop offset="100%" stopColor="rgba(96,165,250,0.1)" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useTranslation();
  const { user, loading, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const from =
    (location.state as { from?: { pathname: string } })?.from?.pathname ||
    "/projects";

  useEffect(() => {
    if (user && !loading) {
      if (!user.onboardingCompleted) {
        navigate("/onboarding", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [user, loading, navigate, from]);

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      setIsSigningIn(true);
      await signInWithGoogle();
    } catch (err) {
      setError(t("login.signInError"));
      console.error("Sign in error:", err);
    } finally {
      setIsSigningIn(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-950">
        <Spinner size="lg" className="text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-12 py-12 bg-white relative">
        {/* Back to home link */}
        <div className="absolute top-6 left-6 sm:left-12">
            <Link
              to="/"
              className="text-sm text-neutral-400 hover:text-neutral-600 transition-colors"
            >
              {t("login.backToHome")}
            </Link>
          </div>

          <div className="w-full max-w-sm">
            {/* Brand */}
            <div className="mb-10">
              <div className="flex items-center gap-2.5 mb-6">
                <AdvisorLogoIcon size="lg" className="text-steel-600" />
                <span className="font-bold text-xl text-steel-600 tracking-tight">
                  ADVisor
                </span>
              </div>
              <h1 className="text-3xl font-bold text-neutral-900 tracking-tight leading-tight">
                {t("login.title1")}
                <span className="block bg-gradient-to-r from-steel-600 to-steel-700 bg-clip-text text-transparent">
                  {t("login.title2")}
                </span>
              </h1>
              <p className="mt-3 text-neutral-500 leading-relaxed">
                {t("login.subtitle")}
              </p>
            </div>

            {/* Sign in with Google */}
            <div className="space-y-4">
              <Button
                variant="outline"
                size="lg"
                className="w-full h-12 text-base font-medium border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 transition-all"
                onClick={handleGoogleSignIn}
                disabled={isSigningIn}
              >
                {isSigningIn ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                {t("login.continueWithGoogle")}
              </Button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-3 text-neutral-400">
                    {t("login.orContinueWithEmail")}
                  </span>
                </div>
              </div>

              {/* Email fallback */}
              {!showEmailForm ? (
                <Button
                  variant="ghost"
                  size="lg"
                  className="w-full h-12 text-neutral-500 hover:text-neutral-700"
                  onClick={() => setShowEmailForm(true)}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t("login.signInWithEmail")}
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              ) : (
                <div className="space-y-3 animate-fade-in">
                  <div>
                    <Label className="text-neutral-600 text-sm">{t("login.email")}</Label>
                    <Input
                      type="email"
                      placeholder="marco@agency.com"
                      inputSize="lg"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-neutral-600 text-sm">{t("login.password")}</Label>
                    <Input
                      type="password"
                      placeholder="Enter your password"
                      inputSize="lg"
                      className="mt-1.5"
                    />
                  </div>
                  <Button
                    size="lg"
                    className="w-full h-11 bg-gradient-to-r from-steel-600 to-steel-700 hover:from-steel-500 hover:to-steel-600 text-white"
                    disabled
                  >
                    {t("login.comingSoon")}
                  </Button>
                  <button
                    onClick={() => setShowEmailForm(false)}
                    className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors w-full text-center"
                  >
                    {t("login.backToGoogle")}
                  </button>
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Human-in-the-loop badge */}
            <div className="mt-8 flex items-center gap-2.5 p-3 rounded-xl bg-neutral-50 border border-neutral-100">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-steel-600/10 to-steel-700/10 flex items-center justify-center shrink-0">
                <Shield className="w-4 h-4 text-steel-600" />
              </div>
              <div>
                <p className="text-xs font-semibold text-neutral-700">
                  {t("login.humanInTheLoop")}
                </p>
                <p className="text-xs text-neutral-400">
                  {t("login.humanInTheLoopDesc")}
                </p>
              </div>
            </div>

            {/* Footer */}
            <p className="mt-8 text-xs text-neutral-300 text-center">
              {t("login.legalText")}
            </p>
            <p className="mt-2 text-xs text-neutral-400 text-center">
              <Link to="/privacy" className="hover:text-neutral-600 underline">
                {t("login.privacyPolicy")}
              </Link>
              <span className="mx-1.5 text-neutral-300">·</span>
              <Link to="/terms" className="hover:text-neutral-600 underline">
                {t("login.terms")}
              </Link>
            </p>
          </div>
        </div>

        {/* Right Side - Visual Proof */}
        <div className="hidden lg:flex flex-1 relative overflow-hidden bg-neutral-950 items-center justify-center">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950" />

          {/* Mesh gradient blobs */}
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-steel-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-steel-700/15 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-600/5 rounded-full blur-3xl" />

          {/* Grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(99,102,241,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.4) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          {/* Agent Loop Visualization */}
          <div className="relative z-10 w-full px-6">
            <AgentLoopVisualization />

            {/* Bottom text */}
            <div className="text-center mt-4">
              <p className="text-white/50 text-sm font-medium">
                {t("login.agentLoopSubtitle")}
              </p>
              <p className="text-white/25 text-xs mt-1.5">
                Perceive &rarr; Plan &rarr; Act &rarr; Report
              </p>
            </div>
          </div>
        </div>
      </div>
  );
}
